import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { useSite } from "hooks/use-site";
import { useNavigate } from "react-router-dom";
import { useAccountQuery } from "../../../features/auth/authApi";

type InitialValues = {
  documentType: string;
  documentNumber: string;
  registrationAuthority: string;
  serviceRequired: string;
  serviceUsage: string;
  hearAboutUs: string;
  employmentStatus: string;
  sourceOfFunds: string;
  industry: string;
  email: string;
  phone: string;
  placeOfBirth: string;
};

const ExtendedIdentificationQuestionnaire = () => {
  const { logo: logoSrc, frontWalletAuthPageColor } = useSite();
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();

  const initialValues: InitialValues = {
    documentType: "",
    documentNumber: "",
    registrationAuthority: "",
    serviceRequired: "",
    serviceUsage: "",
    hearAboutUs: "",
    employmentStatus: "",
    sourceOfFunds: "",
    industry: "",
    email: "",
    phone: "",
    placeOfBirth: "",
  };

  const validationSchema = Yup.object({
    documentType: Yup.string().required("Choose document type"),
    documentNumber: Yup.string().required("Enter document number"),
    serviceRequired: Yup.string().required("Choose service required"),
    serviceUsage: Yup.string().required("Choose goal usage"),
    hearAboutUs: Yup.string().required("Required"),
    employmentStatus: Yup.string().required("Required"),
    sourceOfFunds: Yup.string().required("Required"),
    industry: Yup.string().required("Required"),
    registrationAuthority: Yup.string().required(
      "Enter registration authority"
    ),
    placeOfBirth: Yup.string().required("Enter place of birth"),
  });

  const onSubmit = (values: InitialValues) => {
    const formData = {
      documentType: values.documentType,
      documentNumber: values.documentNumber,
      registrationAuthority: values.registrationAuthority,
      serviceRequired: values.serviceRequired,
      serviceUsage: values.serviceUsage,
      hearAboutUs: values.hearAboutUs,
      employmentStatus: values.employmentStatus,
      sourceOfFunds: values.sourceOfFunds,
      industry: values.industry,
      placeOfBirth: values.placeOfBirth,
    };

    const form1_json = JSON.stringify(formData);
    localStorage.setItem("form1_json", form1_json);
    navigate("/separate-form-upload");
  };

  const saved = account?.user.form1_json;
  let parsed: Partial<InitialValues> = {};
  if (saved) {
    if (typeof saved === "object") {
      parsed = saved;
    } else {
      try {
        parsed = JSON.parse(saved);
      } catch {
        console.warn("Cant parse form1_json");
      }
    }
  }

  const allowed = Object.keys(initialValues) as Array<keyof InitialValues>;
  const filtered: Partial<InitialValues> = {};
  allowed.forEach((key) => {
    if (parsed[key] !== undefined) {
      filtered[key] = parsed[key]!;
    }
  });

  const formInitialValues: InitialValues = {
    ...initialValues,
    ...filtered,
  };

  const { handleSubmit, getFieldMeta, handleChange, handleBlur, isSubmitting } =
    useFormik<InitialValues>({
      initialValues: formInitialValues,
      validationSchema,
      onSubmit,
    });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box
              paddingY={2.5}
              paddingX={4}
              sx={{ bgcolor: frontWalletAuthPageColor }}
            >
              {logoSrc && <img src={logoSrc} alt="logo" width="200" />}
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight="600" ml={1}>
                    Identification & Survey Questionnaire
                  </Typography>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={1}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        Identification Document Type
                      </FormLabel>
                      <RadioGroup
                        row
                        name="documentType"
                        value={getFieldMeta("documentType").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="passport"
                          control={<Radio />}
                          label="Passport"
                        />
                        <FormControlLabel
                          value="id_card"
                          control={<Radio />}
                          label="Identification Card"
                        />
                      </RadioGroup>
                      {getFieldMeta("documentType").touched &&
                        getFieldMeta("documentType").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("documentType").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Passport/ID Number"
                      name="documentNumber"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("documentNumber").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("documentNumber").touched &&
                          getFieldMeta("documentNumber").error
                      )}
                      helperText={
                        getFieldMeta("documentNumber").touched &&
                        getFieldMeta("documentNumber").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="ID Document Registration Authority"
                      name="registrationAuthority"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("registrationAuthority").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("registrationAuthority").touched &&
                          getFieldMeta("registrationAuthority").error
                      )}
                      helperText={
                        getFieldMeta("registrationAuthority").touched &&
                        getFieldMeta("registrationAuthority").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Place of Birth"
                      name="placeOfBirth"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("placeOfBirth").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("placeOfBirth").touched &&
                          getFieldMeta("placeOfBirth").error
                      )}
                      helperText={
                        getFieldMeta("placeOfBirth").touched &&
                        getFieldMeta("placeOfBirth").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        Services Required
                      </FormLabel>
                      <RadioGroup
                        row
                        name="serviceRequired"
                        value={getFieldMeta("serviceRequired").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="exchange"
                          control={<Radio />}
                          label="Exchange"
                        />
                        <FormControlLabel
                          value="wallet"
                          control={<Radio />}
                          label="Wallet"
                        />
                        <FormControlLabel
                          value="both"
                          control={<Radio />}
                          label="Both"
                        />
                      </RadioGroup>
                      {getFieldMeta("serviceRequired").touched &&
                        getFieldMeta("serviceRequired").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("serviceRequired").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        What will you use iLedgends s.r.o. services for?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="serviceUsage"
                        value={getFieldMeta("serviceUsage").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="Investment"
                          control={<Radio />}
                          label="Investment"
                        />
                        <FormControlLabel
                          value="Speculation"
                          control={<Radio />}
                          label="Speculation"
                        />
                        <FormControlLabel
                          value="Payment"
                          control={<Radio />}
                          label="Payment"
                        />
                        <FormControlLabel
                          value="Store_crypto_assets"
                          control={<Radio />}
                          label="Store crypto assets"
                        />
                      </RadioGroup>
                      {getFieldMeta("serviceUsage").touched &&
                        getFieldMeta("serviceUsage").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("serviceUsage").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="How did you hear about us?"
                      name="hearAboutUs"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("hearAboutUs").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("hearAboutUs").touched &&
                          getFieldMeta("hearAboutUs").error
                      )}
                      helperText={
                        getFieldMeta("hearAboutUs").touched &&
                        getFieldMeta("hearAboutUs").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        What is your Employment Status?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="employmentStatus"
                        value={getFieldMeta("employmentStatus").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="Student"
                          control={<Radio />}
                          label="Student"
                        />
                        <FormControlLabel
                          value="Employed"
                          control={<Radio />}
                          label="Employed"
                        />
                        <FormControlLabel
                          value="Unemployed"
                          control={<Radio />}
                          label="Unemployed"
                        />
                        <FormControlLabel
                          value="Retired"
                          control={<Radio />}
                          label="Retired"
                        />
                        <FormControlLabel
                          value="Business_owner"
                          control={<Radio />}
                          label="Business owner"
                        />
                        <FormControlLabel
                          value="Self-employed"
                          control={<Radio />}
                          label="Self-employed"
                        />
                      </RadioGroup>
                      {getFieldMeta("employmentStatus").touched &&
                        getFieldMeta("employmentStatus").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("employmentStatus").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        What is your Source of Wealth?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="sourceOfFunds"
                        value={getFieldMeta("sourceOfFunds").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="Occupation"
                          control={<Radio />}
                          label="Occupation"
                        />
                        <FormControlLabel
                          value="Savings"
                          control={<Radio />}
                          label="Savings"
                        />
                        <FormControlLabel
                          value="Credit_Loan"
                          control={<Radio />}
                          label="Credit/Loan"
                        />
                        <FormControlLabel
                          value="Investments"
                          control={<Radio />}
                          label="Investments"
                        />
                        <FormControlLabel
                          value="Inheritance"
                          control={<Radio />}
                          label="Inheritance"
                        />
                        <FormControlLabel
                          value="Family_or_others"
                          control={<Radio />}
                          label="Family or others"
                        />
                      </RadioGroup>
                      {getFieldMeta("sourceOfFunds").touched &&
                        getFieldMeta("sourceOfFunds").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("sourceOfFunds").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="What industry do you work in?"
                      name="industry"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("industry").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("industry").touched &&
                          getFieldMeta("industry").error
                      )}
                      helperText={
                        getFieldMeta("industry").touched &&
                        getFieldMeta("industry").error
                      }
                    />
                  </Grid>
                </Grid>
                <Stack spacing={2}>
                  <LoadingButton
                    fullWidth
                    loading={isSubmitting}
                    variant="contained"
                    type="submit"
                  >
                    Next Step
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default ExtendedIdentificationQuestionnaire;
